/**
 * Robust geolocation acquisition helper.
 *
 * Motivation (P0 — Anita Currie / Dallas Home Show, multi-talent report
 * 2026-08-18): all four Clock In / Clock Out surfaces
 * (ClockInOut.js, CheckInOutModal.js, Bookings.js, GPSTracker.js) were
 * calling `navigator.geolocation.getCurrentPosition` with
 * `enableHighAccuracy: true` + `timeout: 10000` — a combination that
 * consistently timed out at indoor venues on cellular data across
 * BOTH iOS and Android. Multiple talents got stuck at the "Please
 * enable Location Services" hard-block modal even with permission
 * granted at the OS level.
 *
 * The industry-standard fix is a two-stage acquisition:
 *   1. Ask for a high-accuracy fix with a generous timeout (25s).
 *   2. If it times out or fails with `POSITION_UNAVAILABLE`, fall
 *      back to a low-accuracy (network / cell tower / WiFi) fix
 *      with a short timeout (8s). This is what "Google Maps"-class
 *      apps do — indoor tradeshows, malls, parking garages, etc.
 *
 * A cached fix up to 60s old is acceptable (`maximumAge: 60_000`) so
 * repeat lookups from the same page mount don't burn 25s each time.
 *
 * Returns: `{ latitude, longitude, accuracy, source }` where `source`
 * is 'high_accuracy' | 'low_accuracy' | 'cached'.
 *
 * Throws: an Error with a stable `code` property from
 * `{'unsupported','permission_denied','position_unavailable','timeout','unknown'}`
 * so callers can render targeted error copy.
 */

const HIGH_ACCURACY_TIMEOUT_MS = 25_000;
const LOW_ACCURACY_TIMEOUT_MS   = 8_000;
const MAX_AGE_MS                = 60_000;

const _mapErrorCode = (err) => {
  if (!err) return 'unknown';
  if (typeof err.code === 'string') return err.code;
  return ({
    1: 'permission_denied',
    2: 'position_unavailable',
    3: 'timeout',
  })[err.code] || 'unknown';
};

const _getPosition = (options) => new Promise((resolve, reject) => {
  if (!('geolocation' in navigator)) {
    const e = new Error('Geolocation not supported by this browser.');
    e.code = 'unsupported';
    return reject(e);
  }
  navigator.geolocation.getCurrentPosition(resolve, reject, options);
});

/**
 * Acquire a GPS fix using two-stage strategy.
 *
 * @param {Object} [opts]
 * @param {number} [opts.highAccuracyTimeoutMs]
 * @param {number} [opts.lowAccuracyTimeoutMs]
 * @param {boolean} [opts.skipLowAccuracyFallback] — set true if the
 *   caller must have GPS-level precision (e.g. geofence check-in).
 * @returns {Promise<{latitude:number,longitude:number,accuracy:number,source:string}>}
 */
export async function acquireLocation(opts = {}) {
  const highTimeout = opts.highAccuracyTimeoutMs ?? HIGH_ACCURACY_TIMEOUT_MS;
  const lowTimeout  = opts.lowAccuracyTimeoutMs  ?? LOW_ACCURACY_TIMEOUT_MS;
  const allowFallback = !opts.skipLowAccuracyFallback;

  // Stage 1 — high-accuracy fix (GPS chip + assisted network).
  try {
    const pos = await _getPosition({
      enableHighAccuracy: true,
      timeout: highTimeout,
      maximumAge: MAX_AGE_MS,
    });
    return {
      latitude:  pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy:  pos.coords.accuracy,
      source:    'high_accuracy',
    };
  } catch (highErr) {
    const code = _mapErrorCode(highErr);
    // Permission denied is terminal — bail immediately, don't hit stage 2.
    if (code === 'permission_denied' || code === 'unsupported') {
      const e = new Error(highErr.message || 'Location access unavailable');
      e.code = code;
      throw e;
    }
    if (!allowFallback) {
      const e = new Error(highErr.message || 'Location unavailable — please move to an open area and try again.');
      e.code = code;
      throw e;
    }
    // Fall through to stage 2 for timeout / position_unavailable.
  }

  // Stage 2 — low-accuracy fix (network / cell / WiFi).
  // On indoor venues, this frequently succeeds where high-accuracy times out.
  try {
    const pos = await _getPosition({
      enableHighAccuracy: false,
      timeout: lowTimeout,
      maximumAge: MAX_AGE_MS,
    });
    return {
      latitude:  pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy:  pos.coords.accuracy,
      source:    'low_accuracy',
    };
  } catch (lowErr) {
    const code = _mapErrorCode(lowErr);
    const msg =
      code === 'timeout'              ? 'Location is taking longer than expected. Move to a spot with better signal (near a window / outside) and try again.' :
      code === 'position_unavailable' ? 'Location is unavailable right now. If you are onsite, use the "I\'m onsite" fallback below to request admin verification.' :
      code === 'permission_denied'    ? 'Location access is blocked in your device / browser settings.' :
                                        'Location unavailable — please try again.';
    const e = new Error(msg);
    e.code = code;
    throw e;
  }
}

export const GEO_ERROR_CODES = Object.freeze({
  UNSUPPORTED:          'unsupported',
  PERMISSION_DENIED:    'permission_denied',
  POSITION_UNAVAILABLE: 'position_unavailable',
  TIMEOUT:              'timeout',
  UNKNOWN:              'unknown',
});
